import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { inr } from "../platforms";
import { UploadCloud, Trash2, Download, Loader2, Plus, Upload, FileText } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const inputCls = "border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0047AB]/20";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-slate-600";

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState("");
  const [mapForm, setMapForm] = useState({ heading: "", accounting_head: "" });
  const fileRef = useRef(null);
  const mapFileRef = useRef(null);

  const loadMonths = () => api.get("/expenses/months").then((r) => {
    setMonths(r.data);
    setMonth((m) => m || (r.data[0]?.month ?? ""));
  }).catch(() => {});

  const load = () => {
    const params = {};
    if (month) params.month = month;
    if (stateFilter) params.state_code = stateFilter;
    api.get("/expenses", { params }).then((r) => setExpenses(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, [month, stateFilter]);
  useEffect(() => {
    loadMonths();
    api.get("/expenses/mappings").then((r) => setMappings(r.data)).catch(() => {});
  }, []);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post("/expenses/upload", fd, { timeout: 600000 });
      toast.success(`Processed: ${data.ok} added, ${data.duplicates} duplicates, ${data.failed} failed`, {
        description: data.results.filter((r) => r.status !== "ok").map((r) => `${r.filename}: ${r.message}`).slice(0, 3).join(" | ") || undefined,
        duration: 10000,
      });
      load();
      loadMonths();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadReport = async () => {
    setBusy("report");
    try {
      const params = {};
      if (month) params.month = month;
      const res = await api.get("/expenses/report/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "expense_state_summary.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      const d = e.response?.data;
      if (d instanceof Blob) toast.error(JSON.parse(await d.text()).detail || "Report failed");
      else toast.error(apiError(e));
    } finally { setBusy(""); }
  };

  const addMapping = async (e) => {
    e.preventDefault();
    try {
      await api.post("/expenses/mappings", mapForm);
      toast.success("Mapping saved");
      setMapForm({ heading: "", accounting_head: "" });
      api.get("/expenses/mappings").then((r) => setMappings(r.data));
    } catch (err) { toast.error(apiError(err)); }
  };

  const bulkMappings = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/expenses/mappings/bulk", fd);
      toast.success(`${data.processed} mappings imported`);
      api.get("/expenses/mappings").then((r) => setMappings(r.data));
    } catch (e) { toast.error(apiError(e)); }
  };

  const states = [...new Map(expenses.map((e) => [e.state_code, e.state_name])).entries()];
  const byState = expenses.reduce((acc, e) => {
    const s = acc[e.state_name] || { total: 0, taxable: 0 };
    s.total += e.total;
    s.taxable += e.taxable_total;
    acc[e.state_name] = s;
    return acc;
  }, {});

  return (
    <div data-testid="expenses-page" className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Accounting</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit" }}>Amazon Expenses</h1>
        <p className="text-sm text-slate-500 mt-1">Bulk-upload Amazon expense invoices — AI reads GSTIN, segregates by state and maps fees to accounting heads.</p>
      </div>

      <Tabs defaultValue="invoices">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger data-testid="tab-expense-invoices" value="invoices">Invoices ({expenses.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-expense-mappings" value="mappings">Accounting Heads ({mappings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="space-y-4 mt-4">
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 bg-white flex flex-col sm:flex-row items-center gap-4">
            <span className="w-12 h-12 rounded-lg bg-blue-50 text-[#0047AB] flex items-center justify-center shrink-0">
              {uploading ? <Loader2 size={24} className="animate-spin" /> : <UploadCloud size={24} />}
            </span>
            <div className="flex-1 text-center sm:text-left">
              <div className="font-semibold text-slate-900">Bulk Upload Expense Invoices</div>
              <div className="text-sm text-slate-500">Select multiple PDFs (max 25). Claude AI extracts invoice no., date, GSTIN, fees — and segregates by state.</div>
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple className="hidden"
              data-testid="expense-files-input" onChange={(e) => upload(e.target.files)} />
            <button data-testid="expense-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-60">
              <UploadCloud size={15} /> {uploading ? "Processing..." : "Upload invoices"}
            </button>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className={labelCls}>Month</label>
              <input data-testid="expense-month-input" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={`${inputCls} block mt-1`} />
            </div>
            <select data-testid="expense-state-filter" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className={inputCls}>
              <option value="">All states</option>
              {states.map(([c, n]) => <option key={c} value={c}>{n} ({c})</option>)}
            </select>
            <button data-testid="expense-report-btn" onClick={downloadReport} disabled={busy === "report" || expenses.length === 0}
              className="inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#12233f] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200 disabled:opacity-50">
              {busy === "report" ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} State-wise Summary PDF {month ? `(${month})` : ""}
            </button>
          </div>

          {months.length > 0 && (
            <div data-testid="expense-month-chips" className="flex flex-wrap gap-2">
              <button onClick={() => setMonth("")}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors duration-200 ${!month ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                All months
              </button>
              {months.map((m) => (
                <button key={m.month} data-testid={`month-chip-${m.month}`} onClick={() => setMonth(m.month)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors duration-200 ${month === m.month ? "bg-[#0047AB] text-white border-[#0047AB]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                  {m.month} · {m.count} inv · {inr(m.total)}
                </button>
              ))}
            </div>
          )}

          {Object.keys(byState).length > 0 && (
            <div className="flex flex-wrap gap-3">
              {Object.entries(byState).map(([s, v]) => (
                <div key={s} data-testid={`state-chip-${s}`} className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm">
                  <span className="font-semibold">{s}</span> <span className="font-mono text-slate-600">{inr(v.total)}</span>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">TDS 0.25%: {inr(v.taxable * 0.0025)}</div>
                </div>
              ))}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead><tr className="bg-[#0A192F] text-white text-left">
                {["Invoice No.", "Date", "State", "Issuer", "Heads", "Taxable", "Tax", "Total", ""].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider whitespace-nowrap">{h}</th>))}
              </tr></thead>
              <tbody data-testid="expenses-table-body" className="divide-y divide-slate-100">
                {expenses.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50 transition-colors duration-200">
                    <td className="px-3 py-2.5 font-mono font-semibold whitespace-nowrap">{e.invoice_no}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap">{e.invoice_date}</td>
                    <td className="px-3 py-2.5">{e.state_name} <span className="text-slate-400 font-mono text-xs">({e.state_code})</span></td>
                    <td className="px-3 py-2.5 max-w-44 truncate">{e.seller_name}</td>
                    <td className="px-3 py-2.5 text-xs text-slate-600 max-w-56">
                      {[...new Set(e.items.map((i) => i.accounting_head))].join(", ")}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{inr(e.taxable_total)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{inr(e.cgst + e.sgst + e.igst)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-semibold">{inr(e.total)}</td>
                    <td className="px-3 py-2.5">
                      <button data-testid={`expense-delete-${e.invoice_no}`} onClick={async () => { await api.delete(`/expenses/${e.id}`); load(); loadMonths(); }}
                        className="text-slate-400 hover:text-red-600 transition-colors duration-200"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {expenses.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-slate-400">No expense invoices yet — upload PDFs above.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="mappings" className="space-y-4 mt-4">
          <form onSubmit={addMapping} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-56">
              <label className={labelCls}>Invoice Fee Heading</label>
              <input data-testid="expmap-heading-input" required value={mapForm.heading} onChange={(e) => setMapForm({ ...mapForm, heading: e.target.value })} className={`${inputCls} w-full mt-1`} />
            </div>
            <div className="flex-1 min-w-44">
              <label className={labelCls}>Accounting Head</label>
              <input data-testid="expmap-head-input" required value={mapForm.accounting_head} onChange={(e) => setMapForm({ ...mapForm, accounting_head: e.target.value })} className={`${inputCls} w-full mt-1`} />
            </div>
            <button data-testid="expmap-save-btn" className="inline-flex items-center gap-1.5 bg-[#0047AB] hover:bg-[#003380] text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200"><Plus size={14} /> Add</button>
            <input ref={mapFileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => { bulkMappings(e.target.files[0]); e.target.value = ""; }} />
            <button type="button" data-testid="expmap-bulk-btn" onClick={() => mapFileRef.current?.click()}
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-500 text-slate-700 text-sm font-semibold px-4 py-2 rounded-full transition-colors duration-200">
              <Upload size={15} /> Bulk CSV / Excel
            </button>
          </form>
          <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead><tr className="bg-[#0A192F] text-white text-left">
                {["Invoice Fee Heading", "Accounting Head", ""].map((h) => <th key={h} className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider">{h}</th>)}
              </tr></thead>
              <tbody data-testid="expmap-table-body" className="divide-y divide-slate-100">
                {mappings.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors duration-200">
                    <td className="px-4 py-2.5">{m.heading}</td>
                    <td className="px-4 py-2.5 font-semibold">{m.accounting_head}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button data-testid={`expmap-delete-${m.id}`} onClick={async () => { await api.delete(`/expenses/mappings/${m.id}`); api.get("/expenses/mappings").then((r) => setMappings(r.data)); }}
                        className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
                {mappings.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-slate-400">No mappings — unmapped fees show as "Unmapped" in reports.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
